import React, { useRef, useState } from 'react';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import { ISingleQuestionFeedbackFormApiModel } from './models/single-question-feedback-form.api-model';
import { LanguageService } from '../../../services/language.service';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import ValidationInfo from '../../../validation/validation-info';
import TextFieldFormGroup, { ITextFieldFormGroupState } from '../../Shared/TextFieldFormGroup';
import LogService from '../../../services/log-service/log.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import Alert from '../../Shared/Alert';
import ValidationSummary from '../../Shared/ValidationSummary';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import IValidatable, { IServerValidationError } from '../../Shared/validatable.interface';
import { AnalyticsService } from '../../../services/analytics-service';
import ContainerItemBlockHeading from '../../Shared/ContainerItemBlockHeading';
import { IFormBaseSettings } from '../form-base-props.interface';
import { useTexts } from '../../../hooks/texts.hook';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';

export interface ISingleQuestionFeedbackFormTexts {
  heading: string;
  question: string;
  answerOptionOne: string;
  answerOptionOneText: string;
  answerOptionTwo: string;
  answerOptionTwoText: string;
  answerOptionThree: string;
  answerOptionThreeText: string;
  answerOptionFour: string;
  answerOptionFourText: string;
  answerOptionFive: string;
  answerOptionFiveText: string;
  submit: string;
  submitted: string;
  formsRequiredFieldsMessage: string;
}

export interface ISingleQuestionFeedbackFormSettings extends IFormBaseSettings {
  answerOptionOneUseComment: boolean;
  answerOptionTwoUseComment: boolean;
  answerOptionThreeUseComment: boolean;
  answerOptionFourUseComment: boolean;
  answerOptionFiveUseComment: boolean;
  answerOptionOneCommentRequired: boolean;
  answerOptionTwoCommentRequired: boolean;
  answerOptionThreeCommentRequired: boolean;
  answerOptionFourCommentRequired: boolean;
  answerOptionFiveCommentRequired: boolean;
}

type ISingleQuestionFeedbackFormProps = IServerSideRendered<ISingleQuestionFeedbackFormTexts, ISingleQuestionFeedbackFormSettings>;

function SingleQuestionFeedbackForm({ settings, texts }: ISingleQuestionFeedbackFormProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [selectedOption, setSelectedOption] = useState<number | undefined>(undefined);
  const [commentOneState, setCommentOneState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [commentTwoState, setCommentTwoState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [commentThreeState, setCommentThreeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [commentFourState, setCommentFourState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [commentFiveState, setCommentFiveState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [show, setShow] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);
  const commentOneRef = useRef<IValidatable>(null!);
  const commentTwoRef = useRef<IValidatable>(null!);
  const commentThreeRef = useRef<IValidatable>(null!);
  const commentFourRef = useRef<IValidatable>(null!);
  const commentFiveRef = useRef<IValidatable>(null!);

  // === VARIABLES ===
  const showCommentOne = selectedOption === 1 && settings.answerOptionOneUseComment;
  const showCommentTwo = selectedOption === 2 && settings.answerOptionTwoUseComment;
  const showCommentThree = selectedOption === 3 && settings.answerOptionThreeUseComment;
  const showCommentFour = selectedOption === 4 && settings.answerOptionFourUseComment;
  const showCommentFive = selectedOption === 5 && settings.answerOptionFiveUseComment;
  const isFormValid =
    !!selectedOption &&
    (!showCommentOne || !settings.answerOptionOneCommentRequired || !!commentOneState.value) &&
    (!showCommentTwo || !settings.answerOptionTwoCommentRequired || !!commentTwoState.value) &&
    (!showCommentThree || !settings.answerOptionThreeCommentRequired || !!commentThreeState.value) &&
    (!showCommentFour || !settings.answerOptionFourCommentRequired || !!commentFourState.value) &&
    (!showCommentFive || !settings.answerOptionFiveCommentRequired || !!commentFiveState.value);
  const showSubmitButton = settings.alwaysShowSubmitButton || isFormValid;
  const headingElement = (
    <ContainerItemBlockHeading headingText={texts.heading} headingLevel={settings.headingLevel} headingLevelClass={settings.headingLevelClass} headingVisuallyHidden={settings.headingVisuallyHidden} />
  );
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    const components: ValidatableComponent[] = [];
    if (showCommentOne) {
      components.push({ component: commentOneRef.current, validationInfo: commentOneState.validationInfo, setState: setCommentOneState });
    }
    if (showCommentTwo) {
      components.push({ component: commentTwoRef.current, validationInfo: commentTwoState.validationInfo, setState: setCommentTwoState });
    }
    if (showCommentThree) {
      components.push({ component: commentThreeRef.current, validationInfo: commentThreeState.validationInfo, setState: setCommentThreeState });
    }
    if (showCommentFour) {
      components.push({ component: commentFourRef.current, validationInfo: commentFourState.validationInfo, setState: setCommentFourState });
    }
    if (showCommentFive) {
      components.push({ component: commentFiveRef.current, validationInfo: commentFiveState.validationInfo, setState: setCommentFiveState });
    }
    return components;
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Singlequestionfeedbackform - ${e}`);
    });
  }

  // === EVENT HANDLERS ===
  function handleOptionChange(option: string) {
    setShowErrorMessage(false);
    setSelectedOption(Number(option));
    // Clear all comments
    setCommentOneState({ value: '', validationInfo: new ValidationInfo() });
    setCommentTwoState({ value: '', validationInfo: new ValidationInfo() });
    setCommentThreeState({ value: '', validationInfo: new ValidationInfo() });
    setCommentFourState({ value: '', validationInfo: new ValidationInfo() });
    setCommentFiveState({ value: '', validationInfo: new ValidationInfo() });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedOption || isSubmitting || !honeyPotRef.current.isValid()) return;

    const [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
        AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Singlequestionfeedbackform - ${e}`);
      });
      return;
    }

    setIsSubmitting(true);
    setShowErrorMessage(false);

    let comment = undefined;
    if (showCommentOne) {
      comment = commentOneState.value;
    } else if (showCommentTwo) {
      comment = commentTwoState.value;
    } else if (showCommentThree) {
      comment = commentThreeState.value;
    } else if (showCommentFour) {
      comment = commentFourState.value;
    } else if (showCommentFive) {
      comment = commentFiveState.value;
    }

    try {
      const requestData: ISingleQuestionFeedbackFormApiModel = {
        $type: 'SingleQuestionFeedback',
        blockContentGuid: settings.blockContentGuid,
        pageContentGuid: settings.pageContentGuid,
        answer: selectedOption,
        comment: comment,
      };

      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 400) {
          handleServerValidationErrors((await response.json()).errors);
          setTimeout(() => {
            document.getElementById('single-question-feedback-form-validation-summary')?.focus();
          }, 100);
          return;
        } else {
          throw new Error('Failed to submit feedback');
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      className={`feedback-form single-question-feedback-form ${settings.useBorder ? 'border p-4 rounded' : ''} position-relative ${settings.colorTheme} ${show ? '' : 'd-none'} ${settings.className ?? ''}`}
    >
      {settings.useCloseButton && <button type="button" className="btn-close position-absolute top-0 end-0" aria-label={t('Stäng', 'Close')} onClick={() => setShow(false)} />}
      {isSubmitted && <p className="mb-0">{texts.submitted}</p>}
      {!isSubmitted && (
        <>
          {texts.heading?.trim() && settings.useCloseButton ? <div className="pe-4">{headingElement}</div> : headingElement}
          {(settings.answerOptionOneCommentRequired ||
            settings.answerOptionTwoCommentRequired ||
            settings.answerOptionThreeCommentRequired ||
            settings.answerOptionFourCommentRequired ||
            settings.answerOptionFiveCommentRequired) && (
            <div className="mb-3" role="alert" aria-live="polite">
              {texts.formsRequiredFieldsMessage}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <ValidationSummary id="single-question-feedback-form-validation-summary" validationSummaryErrors={validationSummaryErrors} className={validationSummaryErrors.length > 0 ? 'mb-3' : ''} />
            <RadioFormGroup
              options={[
                { value: '1', text: texts.answerOptionOne },
                { value: '2', text: texts.answerOptionTwo },
                ...(texts.answerOptionThree ? [{ value: '3', text: texts.answerOptionThree }] : []),
                ...(texts.answerOptionFour ? [{ value: '4', text: texts.answerOptionFour }] : []),
                ...(texts.answerOptionFive ? [{ value: '5', text: texts.answerOptionFive }] : []),
              ]}
              rowLayout={true}
              className={`${settings.useCloseButton && !texts.heading?.trim() ? 'pe-4 ' : ''}${showSubmitButton || showCommentOne || showCommentTwo || showCommentThree || showCommentFour || showCommentFive ? 'mb-3' : ''}`.trim()}
              labelText={texts.question}
              state={{ value: selectedOption?.toString() ?? '', validationInfo: new ValidationInfo() }}
              onChange={handleOptionChange}
            />

            {showCommentOne && (
              <div className="mb-3">
                <TextFieldFormGroup
                  ref={commentOneRef}
                  labelText={texts.answerOptionOneText}
                  required={settings.answerOptionOneCommentRequired}
                  inputId={`comment-one-${settings.blockContentGuid}`}
                  className="feedback-form__text-area"
                  serverValidationName="comment"
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={commentOneState}
                  maxLength={500}
                  onChange={(value) => {
                    setCommentOneState({ ...commentOneState, value });
                  }}
                />
              </div>
            )}

            {showCommentTwo && (
              <div className="mb-3">
                <TextFieldFormGroup
                  ref={commentTwoRef}
                  labelText={texts.answerOptionTwoText}
                  required={settings.answerOptionTwoCommentRequired}
                  inputId={`comment-two-${settings.blockContentGuid}`}
                  className="feedback-form__text-area"
                  serverValidationName="comment"
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={commentTwoState}
                  maxLength={500}
                  onChange={(value) => {
                    setCommentTwoState({ ...commentTwoState, value });
                  }}
                />
              </div>
            )}

            {showCommentThree && (
              <div className="mb-3">
                <TextFieldFormGroup
                  ref={commentThreeRef}
                  labelText={texts.answerOptionThreeText}
                  required={settings.answerOptionThreeCommentRequired}
                  inputId={`comment-three-${settings.blockContentGuid}`}
                  className="feedback-form__text-area"
                  serverValidationName="comment"
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={commentThreeState}
                  maxLength={500}
                  onChange={(value) => {
                    setCommentThreeState({ ...commentThreeState, value });
                  }}
                />
              </div>
            )}

            {showCommentFour && (
              <div className="mb-3">
                <TextFieldFormGroup
                  ref={commentFourRef}
                  labelText={texts.answerOptionFourText}
                  required={settings.answerOptionFourCommentRequired}
                  inputId={`comment-four-${settings.blockContentGuid}`}
                  className="feedback-form__text-area"
                  serverValidationName="comment"
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={commentFourState}
                  maxLength={500}
                  onChange={(value) => {
                    setCommentFourState({ ...commentFourState, value });
                  }}
                />
              </div>
            )}

            {showCommentFive && (
              <div className="mb-3">
                <TextFieldFormGroup
                  ref={commentFiveRef}
                  labelText={texts.answerOptionFiveText}
                  required={settings.answerOptionFiveCommentRequired}
                  inputId={`comment-five-${settings.blockContentGuid}`}
                  className="feedback-form__text-area"
                  serverValidationName="comment"
                  autocomplete="off"
                  renderAsTextArea={true}
                  rows={2}
                  state={commentFiveState}
                  maxLength={500}
                  onChange={(value) => {
                    setCommentFiveState({ ...commentFiveState, value });
                  }}
                />
              </div>
            )}

            <Alert show={showErrorMessage} heading={t.generalError} closable={false} className="mb-3" />
            <HoneyPot ref={honeyPotRef} />
            <button hidden={!showSubmitButton} type="submit" className="feedback-form__submit-button btn btn-primary w-100" disabled={!isFormValid || isSubmitting}>
              {texts.submit}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default SingleQuestionFeedbackForm;
